import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Nav, Cover, Footer } from '../components/Chrome';
import { useGetApp } from '../components/GetTheApp';
import { getUserByUsername, getSeriesByAuthor, isPaused } from '../firebase/reads';
import { usePageMeta } from '../usePageMeta';

/**
 * longreads.me/<username> — an author's public shelf.
 *
 * Only books that are active and not private are listed; see
 * `isSeriesPubliclyListable`. Following is an app action.
 */
function Profile() {
  const { username } = useParams();
  const getApp = useGetApp();

  const [state, setState] = useState({ status: 'loading', author: null, series: [] });

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading', author: null, series: [] });

    (async () => {
      try {
        const author = await getUserByUsername(username);
        if (cancelled) return;
        // A paused author reads as "not here" rather than "exists but hidden":
        // confirming the handle is taken would leak exactly what pausing is
        // meant to hide.
        if (!author || isPaused(author)) {
          setState({ status: 'missing', author: null, series: [] });
          return;
        }
        const series = await getSeriesByAuthor(author.id);
        if (cancelled) return;
        setState({ status: 'ready', author, series });
      } catch {
        if (!cancelled) setState({ status: 'error', author: null, series: [] });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [username]);

  const { status, author, series } = state;

  usePageMeta(
    author ? `@${author.username} on Long reads` : 'Long reads',
    author?.longReadsBio || author?.bio || 'Blogs, journals and serials worth staying for.'
  );

  return (
    <div className="app">
      <Nav />
      <main className="page">
        {status === 'loading' && <p className="state">Loading…</p>}
        {status === 'error' && <p className="state">Could not load this profile.</p>}
        {status === 'missing' && <p className="state">No author called @{username}.</p>}

        {status === 'ready' && (
          <>
            <header className="profile-head">
              {author.profile_picture ? (
                <img className="avatar" src={author.profile_picture} alt="" />
              ) : (
                <div className="avatar avatar-fallback">
                  {(author.username || '?').charAt(0).toUpperCase()}
                </div>
              )}
              <h1 className="handle">@{author.username}</h1>
              {(author.longReadsBio || author.bio) && (
                <p className="bio">{author.longReadsBio || author.bio}</p>
              )}
              <p className="meta">
                {series.length} {series.length === 1 ? 'long read' : 'long reads'} ·{' '}
                {author.authorSubscriberCount || 0}{' '}
                {author.authorSubscriberCount === 1 ? 'follower' : 'followers'}
              </p>
              <div className="profile-actions">
                <button className="btn btn-primary" onClick={() => getApp('follow this author')}>
                  Follow
                </button>
              </div>
            </header>

            <h2 className="section-label">LONG READS</h2>
            {series.length === 0 ? (
              <p className="state">Nothing published yet.</p>
            ) : (
              series.map((s) => (
                <Link key={s.id} className="series-card" to={`/read/${s.id}`}>
                  <Cover series={s} />
                  <div>
                    <h3 className="series-title">{s.title}</h3>
                    <p className="series-sub">
                      {s.entryCount || 0} {s.entryCount === 1 ? 'entry' : 'entries'}
                      {s.description ? ` · ${s.description}` : ''}
                    </p>
                  </div>
                </Link>
              ))
            )}
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}

export default Profile;
